import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import io from 'socket.io-client';
import { DASHBOARD_BOT_NAVIGATE_PATH, DASHBOARD_BOT_SECTION_PATH } from '../nav/botNavRoutes';
import { reportDashboardUserNavigated } from '../utils/botSessionBridge';

/**
 * UserCursorTracker - Tracks user cursor movements and clicks
 * Automatically navigates to sections when user clicks on them
 * Reports cursor activity to bot for awareness
 * Listens for bot actions to navigate automatically
 */
const UserCursorTracker = () => {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    let lastReportedSection = null;

    // Connect to backend socket for bot actions
    const socket = io(process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000');
    
    // Listen for bot actions
    socket.on('bot-action', (data) => {
      console.log('🤖 Received bot action:', data.action);
      if (typeof data.action !== 'string' || !data.action.startsWith('navigate-')) return;

      // Trigger bot cursor animation FIRST
      const section = data.action.replace('navigate-', '');
      window.dispatchEvent(new CustomEvent('bot-navigate', { 
        detail: { section } 
      }));
      
      // Navigate on next frame — cursor animates in parallel (was 1800 ms and felt sluggish).
      requestAnimationFrame(() => {
        const path = DASHBOARD_BOT_NAVIGATE_PATH[data.action];
        if (path) navigate(path);
      });
    });

    // Track cursor movement over navigation items
    const handleMouseMove = (e) => {
      const element = document.elementFromPoint(e.clientX, e.clientY);
      
      if (element) {
        // Check if hovering over a navigation button
        const navItem = element.closest('[data-section]') || element.closest('.custio-nav-link') || element.closest('.nav-item');
        
        if (navItem) {
          const section = navItem.getAttribute('data-section') || 
                         navItem.textContent.toLowerCase().trim();
          
          // Report to bot what user is looking at (debounced)
          if (section && section !== lastReportedSection) {
            lastReportedSection = section;
            
            // Add visual feedback
            navItem.style.transform = 'translateX(5px)';
            navItem.style.transition = 'transform 0.3s ease';
            
            // Emit event for bot awareness
            window.dispatchEvent(new CustomEvent('user-cursor-hover', {
              detail: { 
                section,
                element: navItem,
                x: e.clientX,
                y: e.clientY
              }
            }));
            
            console.log(`👆 User hovering over: ${section}`);
          }
        } else {
          // Reset when not hovering over nav
          if (lastReportedSection) {
            lastReportedSection = null;
            // Remove transform from all nav items
            document.querySelectorAll('.custio-nav-link, .nav-item').forEach(item => {
              item.style.transform = '';
            });
          }
        }
      }
    };

    // Track clicks on navigation items
    const handleClick = (e) => {
      const element = e.target.closest('[data-section]') || e.target.closest('.custio-nav-link') || e.target.closest('.nav-item');
      
      if (element) {
        const section = element.getAttribute('data-section') || 
                       element.textContent.toLowerCase().trim();
        
        console.log(`🖱️ User clicked on: ${section}`);
        
        // Visual feedback
        element.style.transform = 'scale(0.95)';
        setTimeout(() => {
          element.style.transform = '';
        }, 150);
        
        // Emit event
        window.dispatchEvent(new CustomEvent('user-cursor-click', {
          detail: { 
            section,
            element,
            x: e.clientX,
            y: e.clientY
          }
        }));
        
        const path = DASHBOARD_BOT_SECTION_PATH[section];
        if (path) {
          reportDashboardUserNavigated(section);
          navigate(path);
        }
      }
    };

    // Add event listeners
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('click', handleClick);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('click', handleClick);
      socket.disconnect();
    };
  }, [navigate, location]);

  return null; // This component doesn't render anything
};

export default UserCursorTracker;
